import { useEffect, useState } from 'react';
import { Layers, ListChecks, Tags, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Aviso } from '@/components/ui/aviso';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { CategoryMultiSelect } from './category-multi-select';
import { BrandMultiSelect } from './brand-multi-select';
import { SkuAutocomplete } from './sku-autocomplete';
import { BuscadorDeExcepciones } from './buscador-de-excepciones';
import { SelectorDeProductos } from './selector-de-productos';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type { Action, ConteosDeReglas, PricingRule } from '../types/pricing-rules.types';
import {
  alcanceListo,
  fraseDeAlcance,
  nombreAutomatico,
  scopeDe,
  type EstadoAlcance,
  type FormaDeAlcance,
} from '../lib/nueva-regla';
import {
  alcanceEnFrase,
  describirReglaCompleta,
  esLimiteEnBs,
  estadoDeRegla,
  tipoDeRegla,
} from '../lib/reglas-en-palabras';
import { usePedidoConEspera } from '../lib/use-pedido-con-espera';
import { hrefDeLaEmpresa } from '@/router/href-de-la-empresa';
import { useAbreElModulo } from '@/features/auth/hooks/use-abre-el-modulo';

/**
 * Paso 2 · A quién (maqueta S3, corregida el 23-sep). Tres formas EXCLUYENTES:
 * todo el catálogo · por categoría y marca · productos puntuales. Más las
 * excepciones. Los números salen de `/simular` con origen HOY (el `resumen` del
 * 4B-datos T7), 400 ms después del último cambio.
 */

const num = formatoCantidad;
const FORMAS: Array<[FormaDeAlcance, string, string, LucideIcon]> = [
  ['CATALOGO', 'Todo el catálogo', 'Todos los productos activos.', Layers],
  ['CATEGORIA_MARCA', 'Por categoría y marca', 'Elegís categorías, marcas o las dos.', Tags],
  ['PUNTUALES', 'Productos puntuales', 'Elegís los productos uno por uno.', ListChecks],
];

export function PasoAQuien({
  alcance,
  onChange,
  acciones,
  editandoId,
  reglas,
  conteos,
}: {
  alcance: EstadoAlcance;
  onChange: (a: EstadoAlcance) => void;
  acciones: Action[];
  editandoId: string | null;
  reglas: PricingRule[];
  conteos: ConteosDeReglas | null;
}) {
  const scope = scopeDe(alcance);
  const [puntualesEnLista, setPuntualesEnLista] = useState(false);
  // Ronda E (T16-4): el enlace a Precios Dinámicos, sólo para quien lo abre.
  const abrePreciosDinamicos = useAbreElModulo('pricing');
  const tipo = tipoDeRegla(acciones);
  // Lote B2 (D6): un límite en Bs es de un solo producto: va directo a «Productos puntuales».
  const enBs = esLimiteEnBs(tipo);
  useEffect(() => {
    // Spec 294 (D-R4-6): uno solo. Si venía con varios elegidos, se vuelve a elegir.
    if (enBs && (alcance.forma !== 'PUNTUALES' || alcance.skus.length > 1))
      onChange({
        ...alcance,
        forma: 'PUNTUALES',
        categories: [],
        brands: [],
        excludedSkus: [],
        skus: alcance.skus.length > 1 ? [] : alcance.skus,
      });
  }, [enBs, alcance, onChange]);
  const cuerpo = alcanceListo(alcance, tipo)
    ? {
        borrador: { name: nombreAutomatico(acciones, scope), scope, actions: acciones },
        ...(editandoId ? { reemplaza: editandoId } : {}),
        origen: 'HOY' as const,
        limit: 1,
      }
    : null;
  const pedido = usePedidoConEspera(cuerpo ? JSON.stringify(cuerpo) : null, () =>
    pricingRulesApi.simular(cuerpo!)
  );
  const resumen = pedido.estado === 'LISTO' ? pedido.data.resumen : null;

  const mismoTipo = reglas.filter((r) => {
    const e = estadoDeRegla(r);
    return r.id !== editandoId && e.corre && e.tipo === tipo;
  });

  const soloCat = alcance.categories.length > 0 && alcance.brands.length === 0;
  const soloMarca = alcance.brands.length > 0 && alcance.categories.length === 0;
  const lasDos = alcance.categories.length > 0 && alcance.brands.length > 0;
  const conMarcaPct =
    conteos && conteos.catalogo.total > 0
      ? Math.round((conteos.catalogo.conMarca / conteos.catalogo.total) * 100)
      : null;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-4">
        {enBs && (
          <Aviso tono="info" className="text-sm">
            Un límite en Bs es sólo para un producto: un monto fijo no sirve para una categoría con
            precios distintos.
          </Aviso>
        )}
        {/* Rediseño de Compite (15-oct): las tres formas como tarjetas, igual que los
            límites del paso 1. El nombre del botón sigue siendo sólo la forma. */}
        <div
          className={cn('grid gap-3 sm:grid-cols-3', enBs && 'hidden')}
          role="radiogroup"
          aria-label="A quién alcanza"
        >
          {FORMAS.map(([f, txt, detalle, Icono]) => {
            const elegida = alcance.forma === f;
            return (
              <label
                key={f}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-xl border bg-card p-4 transition-colors hover:border-primary/50',
                  elegida && 'border-primary bg-primary/5 ring-1 ring-primary hover:border-primary'
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                    elegida ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                  )}
                >
                  <Icono className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-foreground">{txt}</span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">{detalle}</span>
                </span>
                <input
                  type="radio"
                  name="forma-de-alcance"
                  aria-label={txt}
                  checked={elegida}
                  onChange={() => onChange({ ...alcance, forma: f })}
                  className="mt-1 h-4 w-4 shrink-0 accent-primary"
                />
              </label>
            );
          })}
        </div>

        {alcance.forma === 'CATEGORIA_MARCA' && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="mb-1 text-sm font-medium">Categorías</div>
                <CategoryMultiSelect
                  selected={alcance.categories}
                  onChange={(v) => onChange({ ...alcance, categories: v })}
                />
                <p className="mt-1 text-xs text-muted-foreground">Vacío = todas</p>
              </div>
              <div>
                <div className="mb-1 text-sm font-medium">Marcas</div>
                <BrandMultiSelect
                  selected={alcance.brands}
                  onChange={(v) => onChange({ ...alcance, brands: v })}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Vacío = todas
                  {conMarcaPct != null &&
                    ` · la marca está cargada en el ${conMarcaPct} % de los productos`}
                </p>
              </div>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold text-muted-foreground">
                Cómo se combinan
              </div>
              <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-3">
                {[
                  {
                    activa: soloCat,
                    t: 'Sólo categoría',
                    d: 'Televisores → todos los televisores, de cualquier marca',
                  },
                  {
                    activa: soloMarca,
                    t: 'Sólo marca',
                    d: 'Sony → todo Sony: televisores, audio, cámaras…',
                  },
                  {
                    activa: lasDos,
                    t: 'Las dos',
                    d: 'Televisores + Sony → sólo los televisores Sony. Tienen que cumplirse las dos.',
                  },
                ].map((c) => (
                  <div
                    key={c.t}
                    className={cn(
                      'rounded-md border p-2',
                      c.activa && 'border-2 border-primary bg-primary/5'
                    )}
                  >
                    <div className="font-medium">
                      {c.t}
                      {c.activa && ' · lo que elegiste'}
                    </div>
                    {c.d}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {alcance.forma === 'PUNTUALES' && (
          <div>
            <div className="mb-1 text-sm font-medium">Productos</div>
            <SkuAutocomplete
              selected={alcance.skus}
              onChange={(v) => onChange({ ...alcance, skus: v })}
              unoSolo={enBs}
            />
            <div className="mt-2 flex items-center gap-3">
              <Button type="button" variant="outline" onClick={() => setPuntualesEnLista(true)}>
                Elegir de la lista…
              </Button>
              <p className="text-xs text-muted-foreground">
                {enBs
                  ? 'Elegí uno: un monto fijo es para un solo producto.'
                  : 'Para muchos de una misma categoría o marca, mejor elegirla arriba.'}
              </p>
            </div>
            <SelectorDeProductos
              abierto={puntualesEnLista}
              titulo={enBs ? 'Elegir el producto' : 'Elegir los productos'}
              descripcion={
                enBs
                  ? 'Todo el catálogo activo. El límite en Bs es para el que elijas.'
                  : 'Todo el catálogo. La regla alcanza sólo a los que marques.'
              }
              acciones={acciones}
              scope={{}}
              elegidos={alcance.skus}
              textoConfirmar={(n) =>
                enBs ? 'Usar este producto' : n === 1 ? 'Usar 1 producto' : `Usar ${n} productos`
              }
              unoSolo={enBs}
              onConfirmar={(skus) => {
                onChange({ ...alcance, skus });
                setPuntualesEnLista(false);
              }}
              onCerrar={() => setPuntualesEnLista(false)}
            />
          </div>
        )}

        <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="frase-alcance">
          {fraseDeAlcance(alcance, tipo)}
        </div>

        {alcance.forma !== 'PUNTUALES' && (
          <div className="space-y-2 rounded-md border p-3">
            <div className="text-sm font-medium">
              Excepciones{' '}
              <span className="font-normal text-muted-foreground">
                · productos que no entran aunque estén en el alcance · opcional
              </span>
            </div>
            <BuscadorDeExcepciones
              excluidos={alcance.excludedSkus}
              onChange={(v) => onChange({ ...alcance, excludedSkus: v })}
              acciones={acciones}
              alcanceSinExcepciones={scopeDe({ ...alcance, excludedSkus: [] })}
            />
            {alcance.excludedSkus.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Siguen cuidados por las otras reglas que los alcanzan.
              </p>
            )}
          </div>
        )}

        {/* Pulido (spec 232): es para saber, no bueno ni malo; va en azul, no en verde. */}
        {mismoTipo.length > 0 && (
          <Aviso
            tono="info"
            titulo="Estos productos ya pueden tener una regla del mismo tipo"
            className="text-sm"
          >
            <ul className="mt-1 list-disc pl-5 text-xs">
              {mismoTipo.map((r) => (
                <li key={r.id}>
                  «{describirReglaCompleta(r.actions)}» alcanza a {alcanceEnFrase(r.scope ?? {})}.
                </li>
              ))}
            </ul>
            <p className="mt-1 text-xs">
              {tipo === 'REDONDEO'
                ? 'Si dos redondeos alcanzan al mismo producto, se usa el de alcance más puntual.'
                : 'No es un problema: donde alcanzan las dos, rige la más específica (aunque pida menos); si alcanzan igual de puntual, decide la más exigente.'}
            </p>
          </Aviso>
        )}
      </div>

      <aside className="space-y-3">
        <div className="rounded-md border p-4" aria-live="polite">
          <div className="text-sm font-medium text-muted-foreground">Alcanza</div>
          {!cuerpo ? (
            <p className="mt-2 text-sm text-muted-foreground">Elegí al menos un producto.</p>
          ) : pedido.estado === 'ERROR' ? (
            <div className="mt-2 space-y-2 text-sm">
              <p>
                {mensajeDeError(pedido.error, 'No se pudo contar a cuántos alcanza.')} Podés seguir:
                el conteo es una ayuda.
              </p>
              <Button size="sm" variant="outline" onClick={pedido.reintentar}>
                Volver a contar
              </Button>
            </div>
          ) : !resumen ? (
            <p className="mt-2 text-sm text-muted-foreground">contando…</p>
          ) : (
            <>
              <div className="mt-1 text-3xl font-semibold tabular-nums" data-testid="alcanza">
                {num(resumen.alcanzados)}
              </div>
              <p className="text-sm">
                alcanza a {num(resumen.alcanzados)}{' '}
                {resumen.alcanzados === 1 ? 'producto' : 'productos'}
              </p>
              {resumen.alcanzados === 0 && (
                <p className="mt-1 text-sm text-alerta">
                  Así no alcanza a ningún producto. Revisá lo elegido.
                </p>
              )}
              <dl className="mt-3 space-y-1 text-sm">
                {alcance.excludedSkus.length > 0 && (
                  <>
                    <div className="flex justify-between">
                      <dt>sin las excepciones</dt>
                      <dd className="tabular-nums">{num(resumen.alcanzadosSinExcepciones)}</dd>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <dt>menos las excepciones</dt>
                      <dd className="tabular-nums">
                        −{num(resumen.alcanzadosSinExcepciones - resumen.alcanzados)}
                      </dd>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <dt>con costo</dt>
                  <dd className="tabular-nums">{num(resumen.conCosto)}</dd>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <dt>sin costo: ahí el margen no puede cuidar</dt>
                  <dd className="tabular-nums">{num(resumen.sinCosto)}</dd>
                </div>
              </dl>
            </>
          )}
        </div>

        {resumen?.pareceDeLaMarca && resumen.pareceDeLaMarca.total > 0 && (
          <div className="rounded-md border p-4 text-sm">
            <div className="font-medium">Sin marca cargada</div>
            <p className="text-muted-foreground">
              {num(resumen.pareceDeLaMarca.total)}{' '}
              {resumen.pareceDeLaMarca.total === 1 ? 'producto parece' : 'productos parecen'} de{' '}
              {alcance.brands.join(', ')} por el nombre, pero no{' '}
              {resumen.pareceDeLaMarca.total === 1 ? 'tiene' : 'tienen'} marca: no entran. Por
              ejemplo:{' '}
              {resumen.pareceDeLaMarca.ejemplos
                .slice(0, 3)
                .map((e) => e.name)
                .join(' · ')}
              .
            </p>
            {/* 4B-datos, Tarea 12b: la marca se carga en la edición del producto
                (campo «Marca») o con la columna brand del CSV, los dos en Precios
                Dinámicos (Tarea 8). */}
            {abrePreciosDinamicos ? (
              <a
                href={hrefDeLaEmpresa('/dynamic-pricing')}
                className="mt-1 inline-block text-sm font-medium text-primary underline"
              >
                Cargarles la marca
              </a>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                La marca se carga en Precios Dinámicos: pedíselo a quien lo administra.
              </p>
            )}
          </div>
        )}

        {resumen && (
          <div className="rounded-md border p-4 text-sm">
            <div className="font-medium">Estrategias que tocan estos productos</div>
            {resumen.estrategias.porEstrategia.length === 0 ? (
              <p className="text-muted-foreground">
                Ninguna. La regla los cuida igual cuando alguien cambie su precio.
              </p>
            ) : (
              <ul className="text-muted-foreground">
                {resumen.estrategias.porEstrategia.map((e) => (
                  <li key={e.id}>
                    <strong className="text-foreground">{e.name}</strong> · {num(e.productos)} de{' '}
                    {num(resumen.alcanzados)}
                  </li>
                ))}
                {resumen.estrategias.ningunaLosToca > 0 && (
                  <li>{num(resumen.estrategias.ningunaLosToca)} sin estrategia</li>
                )}
              </ul>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
